import { supabase } from '@/lib/supabase';
import { ArxivRecord } from '@/components/admin/content/arxiv/types';

interface CacheQueryParams {
  set?: string;
  from?: string;
  until?: string;
  offset?: number;
  limit?: number;
}

interface CacheQueryResult {
  records: ArxivRecord[];
  total: number;
  isFromCache: true;
}

export const fetchArxivCache = async (params: CacheQueryParams): Promise<CacheQueryResult> => {
  let query = supabase
    .from('arxiv_metadata_cache')
    .select('*', { count: 'exact' })
    .order('published_date', { ascending: false });

  // Apply category filter (set parameter)
  if (params.set) {
    // Categories is a text array, so we need to check if any category starts with the set
    // Use PostgREST array pattern matching - check if any element starts with prefix
    query = query.filter('categories', 'cs', `{${params.set}.*}`);
  }

  // Apply date filters
  if (params.from) {
    query = query.gte('published_date', params.from);
  }
  
  if (params.until) {
    query = query.lte('published_date', params.until);
  }

  // Apply pagination
  if (params.offset) {
    query = query.range(params.offset, (params.offset + (params.limit || 50)) - 1);
  } else if (params.limit) {
    query = query.limit(params.limit);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  // Transform cache records to ArxivRecord format
  const records: ArxivRecord[] = (data || []).map(cacheRecord => ({
    id: cacheRecord.id,
    title: cacheRecord.title,
    authors: Array.isArray(cacheRecord.authors) 
      ? cacheRecord.authors.map((author: any) => 
          typeof author === 'string' ? author : author.name || String(author)
        )
      : [],
    abstract: cacheRecord.abstract || '',
    categories: cacheRecord.categories || [],
    publishedDate: cacheRecord.published_date || '',
    updatedDate: cacheRecord.updated_date || cacheRecord.published_date || '',
    doi: cacheRecord.doi || undefined,
    pdfUrl: cacheRecord.pdf_url || `https://arxiv.org/pdf/${cacheRecord.id}.pdf`,
    versions: cacheRecord.versions || 1,
    license: cacheRecord.license || undefined
  }));

  return {
    records,
    total: count || 0,
    isFromCache: true
  };
};

export const getCacheWindow = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  return {
    from: yesterday.toISOString().split('T')[0],
    until: today.toISOString().split('T')[0]
  };
};

export const isWithinCacheWindow = (from?: string, until?: string): boolean => {
  if (!from || !until) return false;
  
  const cacheWindow = getCacheWindow();
  return from >= cacheWindow.from && until <= cacheWindow.until;
};